import React, { useState, useMemo, useEffect, useCallback } from "react";
import TableActionBtnGroup from "../tableactionbtngroup/TableActionBtnGroup";
import DualPagination from "../../../common/DualPagination";
import PropTypes from "prop-types";
import {
  FaMinusCircle,
  FaPlusCircle,
  FaInfoCircle,
  FaCheckCircle
} from "react-icons/fa";
import {
  useTable,
  useRowSelect,
  useFilters,
  useSortBy,
  usePagination,
  useExpanded
} from "react-table";
import { COLUMNS } from "./Columns";
import { Link, useNavigate } from "react-router-dom";
import { Checkbox } from "../../../../components/common/Checkbox";
import {
  showToast,
  formatDateWithDayjs,
  sanitizeInput
} from "../../../../utils/utils";
import { setLoginAsNameInLocalStorage } from "../../../loginasuser/LoginAsUser";
import {
  PAGE_INDEX,
  PAGE_SIZE_25,
  showToastType
} from "../../../../utils/constants/appConstants";
import {
  action,
  commonErrMsg,
  noDataPresent,
  tooltips,
  userManage,
  UserManagment,
  passwordConfirmModal
} from "../../../../locales/en";
import { useDispatch, useSelector } from "react-redux";
import {
  setProfile,
  userManagementDeactivateThunk,
  userManagementDeleteThunk,
  userManagementGetLoginUserURLThunk,
  userManagementLoadUserDataThunk,
  userManagementResendThunk,
  userManagementSelecteddeletedThunk,
  getProfile
} from "../../../../redux/userManagementSlice";
import {
  ACTIVE,
  TRUE,
  COMPLETED
} from "../../../../utils/constants/appConstants";
import { useVisibleColumnCount } from "../../../../hooks/useVisibleColumnCount";
import TableSkeleton from "../../../skeletons/TableSkeleton";
import UserManagmentActionHeader from "./UserManagmentActionHeader";
import "./usermanagementtable.scss";
import {
  setOnboardingData,
  getOnboardingData
} from "../../../../redux/loginSlice";
import SortableHeaderCell from "../../../common/SortableHeaderCell";
import { useColumnSort } from "../../../../hooks/useColumnSort";
import ListViewFallback from "../../../error-boundary/fallback/ListViewFallback";
import ErrorBoundary from "../../../error-boundary";
import { USER_MANAGEMENT_TOOLTIP_CONFIG } from "../../../../utils/constants/tooltipsConfig";
import { EDIT_ROLE } from "../../../../route/paths";
import {
  CHANGE_PASSWORD,
  EDIT_USER,
  INVOICE,
  VIEW_USER,
  COMPLETE_ACCOUNT_SETUP
} from "../../../../route/paths";
import useResetExpandedOnPagination from "../../../../hooks/useResetExpandedOnPagination";
import PasswordConfModal from "../../../modals/PasswordConfModal";
import { getPasswordConfirmation } from "../../../../redux/invoiceDetailsSlice";
import privateAxios from "../../../../utils/axiosPrivate";
import { getHandleLoginAsPermission } from "../../../../utils/constants/apiEndPoints";
import PasswordSetModal from "../../../modals/PasswordSetModal";
import { useToast } from "../../../../hooks/useToast";

const SelectionHeader = ({ getToggleAllRowsSelectedProps }) => (
  <Checkbox {...getToggleAllRowsSelectedProps()} className="headercheckBox" />
);
SelectionHeader.propTypes = {
  getToggleAllRowsSelectedProps: PropTypes.func
};

const SelectionCellWrapper = ({ row }) => (
  <Checkbox {...row.getToggleRowSelectedProps()} className="bodycheckBox" />
);
SelectionCellWrapper.propTypes = {
  row: PropTypes.object
};

const DATE_COLUMNS = ["createdAt", "updatedAt"];

// Mobile card view: each column renders differently from its accessor
const renderCellValue = (accessor, cellValue, dateValue) => {
  if (accessor === "roles") {
    return (
      <ul className="permissions_content list-inline mb-0">
        {cellValue.map((role) => (
          <li className="list-inline-item mb-1" key={role.id}>
            <Link
              to={EDIT_ROLE(role.id)}
              title={userManage.goToRole}
              className={`badge border border-success text-success rounded-pill ${
                role.fullAccess ? "bg-success text-white" : ""
              }`}
            >
              {role.name}
            </Link>
          </li>
        ))}
      </ul>
    );
  }

  if (DATE_COLUMNS.includes(accessor)) {
    return formatDateWithDayjs(dateValue);
  }

  if (accessor === "isConfirmed") {
    return cellValue === false ? (
      <strong className="text-warning d-inline-block p-0 border-bottom-0">
        <FaInfoCircle />
      </strong>
    ) : (
      <strong className="text-success d-inline-block p-0 border-bottom-0">
        <FaCheckCircle />
      </strong>
    );
  }

  return String(cellValue);
};

const UserManagementTable = () => {
  const toast = useToast();
  const baseColumns = useMemo(() => COLUMNS, []);
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_25);
  const [expandedRows, setExpandedRows] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [userNames, setUserNames] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [enterPassword, setEnterPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [currentRowId, setCurrentRowId] = useState(null);
  const [knowFunction, setKnowFunction] = useState("");
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();
  const [addPasswordModal, setAddPasswordModal] = useState(false);
  const history = useNavigate();
  const { profile: loggedUserData, userManagementLoading } = useSelector(
    (state) => state.userManagement
  );

  const userData = loggedUserData;
  const userPrimaryId = userData?.id;
  const dispatch = useDispatch();

  const initialState = {
    id: null,
    fullname: null,
    primaryEmail: null,
    createdAt: null,
    updatedAt: null
  };

  const { columnOrders, handleSort } = useColumnSort(initialState);
  const sortableColumns = Object.keys(initialState);

  const loadUsersdata = async (pageNumber, size) => {
    try {
      const params = {
        page: pageNumber,
        size,
        query: sanitizeInput(searchQuery) || undefined,
        status: ACTIVE,
        id: columnOrders.id,
        name: columnOrders.fullname,
        email: columnOrders.primaryEmail,
        createdAt: columnOrders.createdAt,
        updatedAt: columnOrders.updatedAt
      };

      const response = await dispatch(
        userManagementLoadUserDataThunk({ params })
      ).unwrap();
      const apiData = response?.data?.data || [];
      setData(apiData);

      const namesById = apiData.reduce((acc, userData) => {
        acc[userData.id] = userData.firstName;
        return acc;
      }, {});
      setUserNames((prev) => ({ ...prev, ...namesById }));

      setTotalPages(response.data.pageInfo.totalPages || 0);
    } catch (error) {
      if (error && error.status === "ERROR") {
        setData([]);
        setTotalPages(1);
      } else {
        setTotalPages(1);
      }
    }
  };

  const SelectionCell = useCallback(
    ({ row }) => <SelectionCellWrapper row={row} />,
    [SelectionCellWrapper]
  );

  const tableColumns = useMemo(() => {
    if (data && data.length > 0) {
      return [
        {
          id: "selection",
          Header: SelectionHeader,
          Cell: SelectionCell
        },
        ...baseColumns
      ];
    }
    return baseColumns;
  }, [data, baseColumns, SelectionCell]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    page,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    selectedFlatRows
  } = useTable(
    {
      columns: tableColumns,
      data,
      manualPagination: true,
      pageCount: totalPages,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE_25 },
      autoResetSelectedRows: false,
      getRowId: (row) => row.id
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const { pageIndex } = state;

  useEffect(() => {
    loadUsersdata(pageIndex, pageSize);
  }, [pageIndex, pageSize, searchQuery, JSON.stringify(columnOrders)]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setTempSearchQuery(value);
    if (value.trim() === "") {
      setSearchQuery("");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      const trimmedQuery = tempSearchQuery.trim();
      gotoPage(0);
      setSearchQuery(trimmedQuery);
    }
  };

  const toggleRow = (rowId) =>
    setExpandedRows((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );

  const handleRowClick = (row) => {
    const rowId = row.id;
    const newSelectedRowIds = new Set(selectedRowIds);

    if (newSelectedRowIds.has(rowId)) {
      newSelectedRowIds.delete(rowId);
    } else {
      newSelectedRowIds.add(rowId);
    }

    setSelectedRowIds(newSelectedRowIds);
  };

  const handleDeleteClick = (rowId) => {
    setCurrentRowId(rowId);
    setKnowFunction("delete");
    setEnterPassword(true);
  };

  const handlePasswordSubmit = async () => {
    try {
      const dataToSend = { password };
      const response = await dispatch(
        getPasswordConfirmation(dataToSend)
      ).unwrap();

      if (response.status === 200) {
        setPassword("");
        setEnterPassword(false);
        if (knowFunction === "delete") {
          await dispatch(
            userManagementDeleteThunk(Number(currentRowId))
          ).unwrap();
        } else if (knowFunction === "batchDelete") {
          const selectedIds = selectedFlatRows
            .map((row) => row.original.id)
            .filter((id) => id !== userPrimaryId);
          await dispatch(
            userManagementSelecteddeletedThunk(selectedIds)
          ).unwrap();
          setSelectedRowIds(new Set());
          state.selectedRowIds = {};
        }
        setSearchQuery("");
        gotoPage(0);
        loadUsersdata(0, pageSize);
      } else {
        showToast(passwordConfirmModal?.validationMessage, showToastType.error);
      }
    } catch (error) {
      showToast(
        error?.response?.data?.message || commonErrMsg.item2,
        showToastType.error
      );
    }
  };

  const handleDeleteSelected = () => {
    if (selectedFlatRows.length === 0) return;
    setKnowFunction("batchDelete");
    setEnterPassword(true);
  };

  const viewUser = (userId) => {
    history(VIEW_USER(userId));
  };

  const handleModalClose = () => {
    setAddPasswordModal(false);
  };

  const handlePasswordEntered = async (password) => {
    try {
      const cleanedPassword = sanitizeInput(password);
      const dataToSend = { password: cleanedPassword };

      const response = await dispatch(
        getPasswordConfirmation(dataToSend)
      ).unwrap();

      if (response.status === 200 || response.status === 201) {
        handleLoginUserClick(currentRowId);
      } else {
        showToast(
          passwordConfirmModal?.validationMessage,
          showToastType?.error
        );
      }
    } catch {
      toast.error(passwordConfirmModal?.validationMessage);
    }
  };

  const handleLoginAsPermissionCheck = async (rowId) => {
    setCurrentRowId(rowId);
    try {
      const response = await privateAxios.post(getHandleLoginAsPermission);
      const hasPermission = response?.data?.data?.hasPermission;
      if (hasPermission) {
        setAddPasswordModal(true);
      }
    } catch (error) {
      toast.error(error);
    }
  };

  const handleLoginUserClick = async (rowId) => {
    try {
      const response = await dispatch(
        userManagementGetLoginUserURLThunk(Number(rowId))
      ).unwrap();

      if (response.status === 200) {
        const { accessToken, refreshToken } = response.data.data;

        if (accessToken && refreshToken) {
          dispatch(setProfile({}));
          setLoginAsNameInLocalStorage(response.data.data);
          dispatch(setOnboardingData({}));

          const [onboardingResult, profileResult] = await Promise.allSettled([
            dispatch(getOnboardingData()).unwrap(),
            dispatch(getProfile()).unwrap()
          ]);

          const onboardingResponse =
            onboardingResult.status === "fulfilled"
              ? onboardingResult.value
              : null;
          const apiResponse =
            profileResult.status === "fulfilled" ? profileResult.value : null;

          const onboardingDetails = onboardingResponse?.data?.data ?? {};
          const onboardingStatus = onboardingDetails?.onboardingStatus;
          const onboardingIsConcent =
            onboardingDetails?.isCreditConsentAccepted;

          const forceApplicationStatus =
            apiResponse?.data?.data?.systemSettings?.forceApplicationProcess;

          if (
            onboardingStatus === COMPLETED &&
            onboardingIsConcent === true &&
            forceApplicationStatus
          ) {
            history(COMPLETE_ACCOUNT_SETUP);
            return;
          }

          history(INVOICE);
        }
      }
    } catch {
      // handle catch here;
    }
  };

  const handleChangePasswordClick = (passId) => {
    history(CHANGE_PASSWORD(passId));
  };

  const handleDeactiveUserClick = async (rowId) => {
    try {
      await dispatch(userManagementDeactivateThunk(Number(rowId))).unwrap();
      setSearchQuery("");
      gotoPage(0);
      loadUsersdata(0, pageSize);
    } catch {
      // handle catch here;
    }
  };

  const handleResendClick = async (rowId) => {
    try {
      await dispatch(userManagementResendThunk(Number(rowId))).unwrap();
    } catch {
      // handle catch here;
    }
  };

  useResetExpandedOnPagination(
    () => setExpandedRows([]),
    [pageIndex, pageSize, searchQuery, JSON.stringify(columnOrders)]
  );

  return (
    <>
      <UserManagmentActionHeader
        handleDeleteSelected={handleDeleteSelected}
        handleKeyDown={handleKeyDown}
        handleSearchChange={handleSearchChange}
        disabled={userManagementLoading}
        selectedFlatRows={selectedFlatRows}
        tempSearchQuery={tempSearchQuery}
        allUsers={TRUE}
        btnText={userManage.batchDelete}
      />
      <ErrorBoundary fallback={<ListViewFallback />}>
        <table
          className="mt-2 mb-3 usermangement-table-wrap"
          {...getTableProps()}
        >
          <thead ref={headerRef}>
            {headerGroups.map((headerGroup) => {
              const { key, ...rest } = headerGroup.getHeaderGroupProps();
              return (
                <tr className="parentrow" {...rest} key={key}>
                  <th />
                  {headerGroup.headers.map((column) => {
                    const isSortable = sortableColumns.includes(column.id);

                    return (
                      <SortableHeaderCell
                        key={column.id}
                        column={column}
                        isSortable={isSortable}
                        columnOrders={columnOrders}
                        handleSort={handleSort}
                      />
                    );
                  })}
                  <th>{action}</th>
                </tr>
              );
            })}
          </thead>
          {userManagementLoading ? (
            <tbody>
              <TableSkeleton rows={14} columns={visibleColumnCount + 1} />
            </tbody>
          ) : (
            <tbody {...getTableBodyProps()}>
              {page.length === 0 ? (
                <tr>
                  <td colSpan={baseColumns.length + 2} className="text-center">
                    {noDataPresent}
                  </td>
                </tr>
              ) : (
                page.map((row) => {
                  prepareRow(row);
                  const isExpanded = expandedRows.includes(row.id);
                  const isSelected = row.isSelected;
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className={`parentrow ${isSelected ? "selected" : ""}`}
                        onClick={() => handleRowClick(row)}
                        {...row.getRowProps()}
                      >
                        <td className="expand-td">
                          <span
                            className="expandbtn cursor-pointer"
                            onClick={(e) => toggleRow(row.id, e)}
                          >
                            {isExpanded ? <FaMinusCircle /> : <FaPlusCircle />}
                          </span>
                        </td>
                        {row.cells.map((cell) => {
                          const cellValue = cell.value;
                          let cellContent = cellValue;

                          if (cell.column.id === "roles") {
                            if (cellValue && cellValue.length > 0) {
                              cellContent = (
                                <ul className="permissions_content list-inline mb-0">
                                  {cellValue.map((role) => (
                                    <li
                                      className="list-inline-item mb-1"
                                      key={role.id}
                                    >
                                      <Link
                                        to={EDIT_ROLE(role.id)}
                                        title={userManage.goToRole}
                                        className={`badge border border-success text-success rounded-pill ${role.fullAccess ? "bg-success text-white" : ""}`}
                                      >
                                        {role.name}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              );
                            } else {
                              cellContent = (
                                <ul className="permissions_content list-inline mb-0">
                                  <li className="list-inline-item">
                                    <span className="badge border border-danger rounded-pill bg-danger">
                                      {userManage?.unassigned}
                                    </span>
                                  </li>
                                </ul>
                              );
                            }
                          }

                          return (
                            <td
                              key={cell.getCellProps().key}
                              {...cell.getCellProps()}
                            >
                              {cell.render("Cell", { value: cellContent })}
                            </td>
                          );
                        })}
                        <td>
                          <TableActionBtnGroup
                            loginUser={
                              userPrimaryId === row.original.id
                                ? null
                                : () =>
                                    handleLoginAsPermissionCheck(
                                      row.original.id,
                                      `${row.original.firstName} ${row.original.lastName}`
                                    )
                            }
                            viewUser={() => viewUser(row.original.id)}
                            editUrl={EDIT_USER(row.original.id)}
                            changePassword={() =>
                              handleChangePasswordClick(row.original.id)
                            }
                            deactivateUser={
                              userPrimaryId === row.original.id
                                ? null
                                : () => handleDeactiveUserClick(row.original.id)
                            }
                            reSend={
                              row.original.isConfirmed === false
                                ? () => handleResendClick(row.original.id)
                                : null
                            }
                            onDelete={
                              userPrimaryId === row.original.id
                                ? null
                                : () => handleDeleteClick(row.original.id)
                            }
                            tooltips={{
                              ...USER_MANAGEMENT_TOOLTIP_CONFIG,
                              loginUser: {
                                ...USER_MANAGEMENT_TOOLTIP_CONFIG.loginUser,
                                id: `tooltip-login-${row.original.id}`,
                                content: `${tooltips.loginAs} ${userNames[row.original.id] || tooltips.user}`
                              }
                            }}
                          />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="childrow colexpand">
                          <td colSpan={baseColumns.length + 2}>
                            <div className="tdbody">
                              {baseColumns.map((column) => {
                                const accessor = column.accessor;
                                const cellValue = row.original[accessor];
                                const dateValue =
                                  accessor === "updatedAt" && !cellValue
                                    ? row.original.createdAt
                                    : cellValue;
                                return (
                                  <span
                                    key={accessor}
                                    data-label={column.Header}
                                  >
                                    {renderCellValue(
                                      accessor,
                                      cellValue,
                                      dateValue
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          )}
        </table>
      </ErrorBoundary>
      {data.length > 0 && (
        <DualPagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
          dataLength={data.length}
        />
      )}
      {enterPassword && (
        <PasswordConfModal
          title={
            UserManagment?.financialsTab?.aiCreditScoreSection?.passwordModal
              ?.deleteUser
          }
          content={
            UserManagment?.financialsTab?.aiCreditScoreSection?.passwordModal
              ?.subTitle
          }
          isOpen={enterPassword}
          onClose={() => {
            setEnterPassword(false);
            setPassword("");
          }}
          onSubmit={handlePasswordSubmit}
          password={password}
          setPassword={setPassword}
        />
      )}

      <PasswordSetModal
        isOpen={addPasswordModal}
        toggle={handleModalClose}
        handlePasswordEntered={handlePasswordEntered}
      />
    </>
  );
};

export default UserManagementTable;
